// src/Pages/Assets/futuresComponentes/pnlChart.jsx
import React, { useMemo } from "react";
import { Paper, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  ReferenceLine,
  defs,
  linearGradient,
  stop,
} from "recharts";
import { dailyRealized, fmt } from "./pnlUtils";

function TooltipBox({ active, payload, label }) {
  if (!active || !payload) return null;
  return (
    <Paper sx={{ p: 1, borderRadius: 1.5 }}>
      <Typography variant="caption" sx={{ fontWeight: 800 }}>
        {label}
      </Typography>
      {payload.map((p) => (
        <Typography key={p.dataKey} variant="caption" sx={{ display: "block" }}>
          {p.name}: {typeof p.value === "number" ? fmt(p.value, 2) : p.value}
        </Typography>
      ))}
    </Paper>
  );
}

export default function PnLChart({ brand, historyRows }) {
  const theme = useTheme();
  const gridCol = alpha(theme.palette.text.primary, 0.06);

  const dailies = useMemo(() => dailyRealized(historyRows), [historyRows]);
  const data = useMemo(
    () =>
      dailies.map((d) => ({
        date: d.date,
        DailyPnL: d.pnl,
        CumulativePnL: d.cum,
      })),
    [dailies]
  );

  return (
    <Paper sx={{ p: 2, borderRadius: 2, mb: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 900, mb: 1 }}>
        PnL & Growth
      </Typography>
      <div style={{ width: "100%", height: 260 }}>
        <ResponsiveContainer>
          <ComposedChart
            data={data}
            margin={{ top: 10, right: 16, bottom: 0, left: -8 }}
          >
            <CartesianGrid stroke={gridCol} vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => fmt(v, 0)} />
            <Tooltip content={<TooltipBox />} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <ReferenceLine
              y={0}
              stroke={alpha(theme.palette.text.primary, 0.3)}
            />

            {/* Gradient for area */}
            <defs>
              <linearGradient id="cumFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={brand} stopOpacity={0.35} />
                <stop offset="100%" stopColor={brand} stopOpacity={0.05} />
              </linearGradient>
            </defs>

            <Bar dataKey="DailyPnL" name="Daily PnL" barSize={14} />
            <Area
              dataKey="CumulativePnL"
              name="Cumulative PnL"
              type="monotone"
              fill="url(#cumFill)"
              stroke={brand}
              strokeWidth={2}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </Paper>
  );
}
